import {Suggestions} from "@/pages/dashboard/mentee/suggestions";
import {useQuery} from "@tanstack/react-query";
import {queryKeys} from "@/services/queryKeyFactory.ts";
import {getMenteeById, getMenteeByIdWithSuggestedMentors, getMenteePreferenceStats} from "@/services/menteeService.ts";
import {Skeleton} from "@/components/ui/skeleton.tsx";
import {Trans, useTranslation} from "react-i18next";
import {isMentorSelectionPeriod, isPreferenceSubmissionPeriod} from "@/utils/time-helper.ts";
import TaskFlow from "@/components/TaskFlow.tsx";
import MentorSessionsBlock from "@/pages/dashboard/mentor/MentorSessionsBlock.tsx";
import { NewBadge } from "@/components/ui/new-badge";
import {Card, CardContent, CardHeader, CardTitle} from "@/components/ui/card.tsx";
import {Link} from "react-router-dom";
import {getImgSrc} from "@/utils/file-upload.ts";
import {getLocalizedFieldName} from "@/utils/translation-helper.ts";
import {MentorWithoutAssociations} from "@/types/Mentor.ts";
import MentorInfoCard from "@/components/MentorInfoCard.tsx";
import { DashboardStatsChart } from "@/components/DashboardStatsChart.tsx";
import {PageTitle, SectionTitle} from "@/components/ui/headings";

type MenteeDashboardProps = {
    menteeId: number,
    maxNumberOfMentees: number,
    submitPreferenceDeadline: Date | undefined,
    selectMentorDeadline: Date | undefined,
}

const MenteeDashboardPage = ({
                                 menteeId,
                                 maxNumberOfMentees,
                                 submitPreferenceDeadline,
                                 selectMentorDeadline
                             }: MenteeDashboardProps) => {
    const {t} = useTranslation();
    const today = new Date();

    const {data: mentee, isFetching} = useQuery({
        queryKey: queryKeys.fetchMentees.one,
        queryFn: () => getMenteeById(menteeId),
        initialData: null,
    });

    const {data: menteeWithSuggestedMentors} = useQuery({
        queryKey: queryKeys.fetchSuggestions.byMenteeId,
        queryFn: () => getMenteeByIdWithSuggestedMentors(menteeId),
        enabled: isMentorSelectionPeriod(submitPreferenceDeadline, selectMentorDeadline), //only run this query if we are in "mentor-selection" period
    });

    const {data: preferenceStats} = useQuery({
        queryKey: queryKeys.fetchMentees.preferenceStats,
        queryFn: getMenteePreferenceStats,
        enabled: isPreferenceSubmissionPeriod(submitPreferenceDeadline),
    });

    if (isFetching || !mentee) {
        return (
            <div className="container pt-12">
                <div className="space-y-4">
                    <Skeleton className="h-8 w-2/3 max-w-md" />
                    <Skeleton className="h-4 w-full max-w-xl" />
                    <Skeleton className="h-4 w-full max-w-lg" />
                    <Skeleton className="h-4 w-full max-w-xl" />
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="container pt-12">
                <div className="md:flex no-wrap md:-mx-2 gap-4">
                    <div className="w-full md:w-9/12 md:mx-2">
                        {isPreferenceSubmissionPeriod(submitPreferenceDeadline) && (
                            <div className="mb-8 space-y-6">
                                <Card>
                                    <CardHeader className="flex flex-row items-start justify-between space-y-0 gap-4">
                                        <CardTitle className="text-xl font-semibold">{t("dashboard.introducingAffinityGroups")}</CardTitle>
                                        <NewBadge>{t("common.new")}</NewBadge>
                                    </CardHeader>
                                    <CardContent className="space-y-4 text-base text-text-2">
                                        <p>
                                            <Trans i18nKey="dashboard.affinityGroupsIntro1" components={{ strong: <strong className="text-text-1" /> }} />
                                        </p>
                                        <p>
                                            {t("dashboard.affinityGroupsIntro2")}
                                        </p>
                                    </CardContent>
                                </Card>

                                <Card>
                                    <CardContent className="p-8">
                                        <PageTitle>
                                            {mentee.menteePreference
                                                ? t("dashboard.thanksForProfileSubmission")
                                                : t("dashboard.promptToSubmitPreference", {deadline: submitPreferenceDeadline?.toDateString()})}
                                        </PageTitle>
                                        <p className="mt-4 text-base text-text-2">
                                            <Link to={`/mentees/${mentee.id}/preference`} className="text-text-1 underline underline-offset-4 hover:text-springGreen transition-colors">
                                                {t("dashboard.submitEditPreference")}
                                            </Link>
                                            {submitPreferenceDeadline && (
                                                <span> {t("dashboard.beforeDeadline", {deadline: submitPreferenceDeadline.toDateString()})}</span>
                                            )}
                                        </p>
                                        {!mentee.menteePreference && (
                                            <Link className="mt-6 inline-flex h-10 items-center rounded-md bg-text-1 px-4 py-2 text-sm font-semibold text-bg-card hover:opacity-95 focus-visible:ring-2 focus-visible:ring-accent/40 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-card" to={`/mentees/${mentee.id}/preference`}>
                                                {t("preference.submit")}
                                            </Link>
                                        )}
                                    </CardContent>
                                </Card>

                                {preferenceStats && (
                                    <DashboardStatsChart
                                        data={{
                                            completedCount: preferenceStats.completedCount,
                                            totalCount: preferenceStats.totalMentees,
                                            percentChangeToday: preferenceStats.percentChangeToday,
                                            timeSeries: preferenceStats.timeSeries,
                                        }}
                                        statDescriptionKey="dashboard.menteesSubmittedPreference"
                                        totalLabelKey="dashboard.outOfTotalMentees"
                                        chartLabelKey="dashboard.preferenceSubmissionsOverTime"
                                    />
                                )}
                            </div>
                        )}

                        {mentee.mentor && (
                            <div className="mb-8">
                                <MentorInfoCardBlock mentor={mentee.mentor} />
                            </div>
                        )}

                        {isMentorSelectionPeriod(submitPreferenceDeadline, selectMentorDeadline) && menteeWithSuggestedMentors && (
                                <div className="mb-8 space-y-2">
                                    <SectionTitle>{t("dashboard.suggestedMentors")}</SectionTitle>
                                    <p className="text-sm text-text-2">
                                        {t("dashboard.suggestedMentorsDesc")}
                                    </p>

                                    <Suggestions menteeWithSuggestedMentors={menteeWithSuggestedMentors}
                                                 maxNumberOfMentees={maxNumberOfMentees}
                                                 selectMentorDeadline={selectMentorDeadline}/>
                                </div>
                            )
                        }

                        {selectMentorDeadline && today > selectMentorDeadline && mentee.mentor && (
                            <div className="mb-8 w-full">
                                <MentorSessionsBlock mentor={mentee.mentor} menteeId={menteeId} />
                            </div>
                        )}
                    </div>
                    <div className="w-full md:w-3/12 md:mx-2">
                        <TaskFlow mentee={mentee}
                                  submitPreferenceDeadline={submitPreferenceDeadline}
                                  selectMentorDeadline={selectMentorDeadline}
                        />
                    </div>
                </div>
            </div>
        </>
    )
}

function MentorInfoCardBlock({ mentor }: { mentor: MentorWithoutAssociations }) {
    const { t } = useTranslation();
    const profileSrc = getImgSrc(mentor.user?.profilePictureSrc ?? undefined);
    const mentorTitle = mentor ? (mentor[getLocalizedFieldName("title") as keyof MentorWithoutAssociations] as string) : "";
    const mentorUniversity = mentor ? (mentor[getLocalizedFieldName("university") as keyof MentorWithoutAssociations] as string) : "";
    const mentorCareerFocus = mentor?.careerFocus ? (mentor.careerFocus[getLocalizedFieldName("name") as keyof typeof mentor.careerFocus] as string) : "";
    
    // Construct fullName from firstName and lastName if fullName is not available
    const mentorFullName = mentor.user?.fullName ?? 
        (mentor.user?.firstName && mentor.user?.lastName 
            ? `${mentor.user.firstName} ${mentor.user.lastName}`.trim()
            : mentor.user?.firstName ?? mentor.user?.lastName ?? t("common.mentor"));

    return (
        <div className="w-full">
            <SectionTitle className="mb-4">{t("common.mentor")}</SectionTitle>
            <MentorInfoCard
                mentorId={mentor.id}
                fullName={mentorFullName}
                profileSrc={profileSrc}
                title={mentorTitle}
                university={mentorUniversity}
                careerFocus={mentorCareerFocus}
            />
        </div>
    );
}

export default MenteeDashboardPage;
